/**
 * Pure helpers for the truthful async-operation UI on the
 * Feasibility Study page.  Extracted so focused Jest coverage
 * doesn't have to import the full page tree (react-router-dom v7 is
 * ESM-only and can't be resolved from the CRA jest transform).
 *
 * The single source of truth for the operation lifecycle is the
 * backend row.  These helpers only shape it for display; nothing
 * here fabricates milestones, percent progress, or narration.
 */

export const MILESTONES = [
    { stage: "queued",            label: "Research queued" },
    { stage: "worker_started",    label: "Worker started" },
    { stage: "preparing_inputs",  label: "Preparing research inputs" },
    { stage: "provider_running",  label: "Running live research" },
    { stage: "validating",        label: "Validating research output" },
    { stage: "committing",        label: "Saving research" },
];

export const STAGE_INDEX = Object.fromEntries(
    MILESTONES.map((m, i) => [m.stage, i]));

export const RESEARCH_KINDS = new Set([
    "generate", "research_again", "refine",
]);

// Terminal statuses — the operation will not be re-queued and its
// dialog can be dismissed by the user.  Crucially `failed` (without
// `_permanent`) is TRANSIENT — the sweeper will re-queue it after
// `next_retry_at`, so from the UI's perspective it must count as
// active (addendum retry pass §C: "Provider rate limit reached —
// retrying shortly…").
export const TERMINAL_STATUSES = new Set([
    "succeeded", "failed_permanent",
]);

const TRANSIENT_FAILED = "failed";
const BLOCKED_DAILY_BUDGET = "blocked_daily_budget";

export const STAGE_AWAITING_RETRY = "awaiting_retry";

export function isActiveOp(op) {
    if (!op) return false;
    if (op.status === "pending" || op.status === "processing") return true;
    // Transient failure with a scheduled retry — the dialog stays
    // open showing the truthful wait state.  A `failed` row with no
    // `next_retry_at` is treated as terminal (defensive).
    if (op.status === TRANSIENT_FAILED && op.next_retry_at) return true;
    // Daily-budget block — sweeper auto-resumes at UTC midnight; the
    // dialog stays open so the operator can see the friendly copy.
    if (op.status === BLOCKED_DAILY_BUDGET) return true;
    return false;
}

export function isRetryScheduled(op) {
    return !!(op && op.status === TRANSIENT_FAILED && op.next_retry_at);
}

export function isBlockedDailyBudget(op) {
    return !!(op && op.status === BLOCKED_DAILY_BUDGET);
}

export function formatElapsed(seconds) {
    if (seconds == null || !Number.isFinite(seconds) || seconds < 0) {
        return "0s";
    }
    const s = Math.floor(seconds);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${String(m).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

/**
 * Elapsed seconds derived from persisted backend timestamps.
 *
 * - Running: `(nowMs - started_at)` — live ticker updates.
 * - Terminal: `finished_at - started_at` — frozen.
 * - Not yet started: `0`.
 *
 * `nowMs` is injected so callers can freeze the clock in tests and
 * so the elapsed value is derived from the SAME `nowMs` React uses
 * to schedule its 1-second tick.
 */
export function computeOpElapsedSeconds(op, nowMs) {
    if (!op) return 0;
    const started = op.started_at ? Date.parse(op.started_at) : null;
    if (!started || Number.isNaN(started)) return 0;
    if (op.finished_at && TERMINAL_STATUSES.has(op.status)) {
        const finished = Date.parse(op.finished_at);
        if (Number.isNaN(finished)) return 0;
        return Math.max(0, Math.floor((finished - started) / 1000));
    }
    const now = nowMs != null ? nowMs : Date.now();
    return Math.max(0, Math.floor((now - started) / 1000));
}

/**
 * Derive milestone-timeline items for the current op:
 *
 * - Reached stages render as `done` (✓).
 * - The current stage renders as `running` (●) only while the op is
 *   still processing/pending; on terminal (success or failure) it
 *   is no longer a running dot.
 * - Future stages render as `pending` (○).
 * - On terminal `succeeded`, every stage is `done`.
 */
export function buildMilestoneTimeline(op) {
    if (!op) {
        return MILESTONES.map((m) => ({ ...m, state: "pending" }));
    }
    const reached = new Set(
        (op.milestone_history || []).map((h) => h.stage));
    if (op.stage) reached.add(op.stage);
    const succeeded = op.status === "succeeded";
    const failed = op.status === "failed"
                    || op.status === "failed_permanent";
    const running = !TERMINAL_STATUSES.has(op.status);
    const currentIdx = STAGE_INDEX[op.stage] ?? -1;
    return MILESTONES.map((m, i) => {
        if (succeeded) return { ...m, state: "done" };
        if (reached.has(m.stage) && i !== currentIdx) {
            return { ...m, state: "done" };
        }
        if (i === currentIdx && running) return { ...m, state: "running" };
        if (i === currentIdx && failed) return { ...m, state: "done" };
        if (reached.has(m.stage)) return { ...m, state: "done" };
        return { ...m, state: "pending" };
    });
}
